import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const BlockchainComponent = ({ user }) => {
  const [tab, setTab] = useState('chain');
  const [blocks, setBlocks] = useState([]);
  const [stats, setStats] = useState(null);
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [searchTx, setSearchTx] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [pendingTxs, setPendingTxs] = useState([]);
  const [mining, setMining] = useState(false);
  const [difficultyInput, setDifficultyInput] = useState(2);

  const isAdmin = user?.email === 'admin@cualapp.com';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [chainRes, statsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/blockchain/chain?limit=20`),
        axios.get(`${API}/blockchain/stats`),
        axios.get(`${API}/blockchain/pending`)
      ]);
      setBlocks(chainRes.data.blocks || []);
      setStats(statsRes.data);
      setPendingTxs(pendingRes.data.pending || []);
      if (statsRes.data?.current_difficulty) setDifficultyInput(statsRes.data.current_difficulty);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const verifyChain = async () => {
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/blockchain/verify`);
      setVerification(res.data);
    } catch (err) { console.error(err); }
    finally { setVerifying(false); }
  };

  const forceMine = async () => {
    setMining(true);
    try {
      const res = await axios.post(`${API}/blockchain/mine`);
      alert(`${res.data.message}${res.data.mining_stats ? ` (${res.data.mining_stats.time_seconds}s, ${res.data.mining_stats.attempts} intentos, ${res.data.mining_stats.hash_rate} H/s)` : ''}`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setMining(false); }
  };

  const setDifficulty = async () => {
    try {
      const res = await axios.post(`${API}/blockchain/difficulty`, { difficulty: difficultyInput });
      alert(res.data.message);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const searchTransaction = async () => {
    if (!searchTx.trim()) return;
    try {
      const res = await axios.get(`${API}/blockchain/tx/${searchTx}`);
      setSearchResult(res.data);
    } catch (err) { setSearchResult({ found: false, message: 'Error buscando' }); }
  };

  const txTypeColor = (type) => {
    const colors = {
      genesis: 'bg-purple-100 text-purple-700',
      transfer: 'bg-blue-100 text-blue-700',
      pago_movil: 'bg-green-100 text-green-700',
      escrow_hold: 'bg-amber-100 text-amber-700',
      escrow_release: 'bg-emerald-100 text-emerald-700',
      smart_contract_partial_release: 'bg-indigo-100 text-indigo-700',
      payout: 'bg-teal-100 text-teal-700',
      marketplace: 'bg-pink-100 text-pink-700',
      ride_payment: 'bg-cyan-100 text-cyan-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando blockchain...</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="blockchain-module">
      {/* Enhanced Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total_blocks}</p>
            <p className="text-xs text-gray-500 mt-1">Bloques</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total_transactions}</p>
            <p className="text-xs text-gray-500 mt-1">Transacciones</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.pending_transactions}</p>
            <p className="text-xs text-gray-500 mt-1">Pendientes</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-indigo-600">{'0'.repeat(stats.current_difficulty)}</p>
            <p className="text-xs text-gray-500 mt-1">Dificultad (d={stats.current_difficulty})</p>
          </div>
        </div>
      )}

      {/* Mining Performance */}
      {stats?.mining_averages?.blocks_mined > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-4" data-testid="mining-stats">
          <h3 className="text-sm font-semibold text-amber-800 mb-2">Rendimiento de Minado</h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-lg font-bold text-amber-700">{stats.mining_averages.avg_time_seconds}s</p>
              <p className="text-xs text-amber-600">Tiempo prom.</p>
            </div>
            <div>
              <p className="text-lg font-bold text-amber-700">{stats.mining_averages.avg_hash_rate.toLocaleString()}</p>
              <p className="text-xs text-amber-600">Hash/s prom.</p>
            </div>
            <div>
              <p className="text-lg font-bold text-amber-700">{stats.mining_averages.blocks_mined}</p>
              <p className="text-xs text-amber-600">Bloques minados</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['chain', 'pending', 'verify', 'search', ...(isAdmin ? ['config'] : [])].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {t === 'chain' ? 'Cadena' : t === 'pending' ? `Pool (${pendingTxs.length})` : t === 'verify' ? 'Verificar' : t === 'search' ? 'Buscar TX' : 'Config'}
          </button>
        ))}
        {stats?.pending_transactions > 0 && (
          <button onClick={forceMine} disabled={mining} data-testid="mine-btn"
            className="ml-auto px-4 py-2 rounded-lg text-sm font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors disabled:opacity-50">
            {mining ? 'Minando...' : `Minar (${stats.pending_transactions})`}
          </button>
        )}
      </div>

      {/* Chain View - Enhanced */}
      {tab === 'chain' && (
        <div className="space-y-2" data-testid="chain-view">
          {blocks.map((block, i) => (
            <div key={block.index} data-testid={`block-${block.index}`}>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => setExpandedBlock(expandedBlock === block.index ? null : block.index)}
                  className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold ${
                      block.index === 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      #{block.index}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">
                        {block.index === 0 ? 'Bloque Genesis' : `Bloque #${block.index}`}
                        <span className="ml-2 text-xs text-gray-400">{block.tx_count || block.transactions?.length || 0} tx</span>
                        {block.difficulty && <span className="ml-1 text-xs text-amber-500">d={block.difficulty}</span>}
                      </p>
                      <p className="text-xs font-mono text-gray-400">{block.hash?.slice(0, 24)}...</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{block.timestamp ? new Date(block.timestamp).toLocaleString('es-VE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                    <p className="text-xs text-gray-300">nonce: {block.nonce?.toLocaleString()}</p>
                  </div>
                </button>

                {expandedBlock === block.index && (
                  <div className="px-5 pb-4 border-t border-gray-100 pt-3 space-y-3">
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <span className="text-gray-400 block mb-0.5">Hash:</span>
                        <span className="font-mono text-gray-700 break-all text-[11px]">{block.hash}</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <span className="text-gray-400 block mb-0.5">Hash anterior:</span>
                        <span className="font-mono text-gray-700 break-all text-[11px]">{block.previous_hash}</span>
                      </div>
                      {block.merkle_root && (
                        <div className="bg-gray-50 rounded-lg p-2.5">
                          <span className="text-gray-400 block mb-0.5">Merkle Root:</span>
                          <span className="font-mono text-indigo-600 break-all text-[11px]">{block.merkle_root}</span>
                        </div>
                      )}
                    </div>

                    {block.mining_stats && (
                      <div className="flex gap-3 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                        <span>Tiempo: {block.mining_stats.time_seconds}s</span>
                        <span>Intentos: {block.mining_stats.attempts?.toLocaleString()}</span>
                        <span>{block.mining_stats.hash_rate?.toLocaleString()} H/s</span>
                      </div>
                    )}

                    {block.transactions?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Transacciones ({block.transactions.length})</p>
                        <div className="space-y-1.5">
                          {block.transactions.map((tx, j) => (
                            <div key={j} className="bg-gray-50 rounded-lg px-3 py-2 text-xs flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${txTypeColor(tx.type)}`}>{tx.type}</span>
                                <span className="text-gray-500 truncate">{tx.from?.slice(0, 8)}... → {tx.to?.slice(0, 8)}...</span>
                                {tx.description && <span className="text-gray-400 truncate hidden sm:inline">| {tx.description?.slice(0, 30)}</span>}
                              </div>
                              <span className="font-semibold text-gray-900 flex-shrink-0">{tx.amount > 0 ? `${tx.amount} ${tx.currency || 'Cc'}` : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Visual chain link between blocks */}
              {i < blocks.length - 1 && (
                <div className="flex items-center justify-center py-1">
                  <div className="flex flex-col items-center">
                    <div className="w-0.5 h-3 bg-gray-300"></div>
                    <div className="w-3 h-3 rounded-full border-2 border-gray-300 bg-white"></div>
                    <div className="w-0.5 h-3 bg-gray-300"></div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {blocks.length === 0 && <div className="text-center text-gray-400 py-8 text-sm">Blockchain vacia</div>}
        </div>
      )}

      {/* Pending Pool */}
      {tab === 'pending' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="pending-view">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Pool de Transacciones Pendientes</h3>
            <span className="text-xs text-gray-400">
              {pendingTxs.length}/{stats?.block_size || 10} para auto-minar
            </span>
          </div>
          {pendingTxs.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No hay transacciones pendientes</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {pendingTxs.map((tx, i) => (
                <div key={i} className="px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${txTypeColor(tx.type)}`}>{tx.type}</span>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-700 truncate">{tx.from?.slice(0, 10)}... → {tx.to?.slice(0, 10)}...</p>
                      {tx.description && <p className="text-xs text-gray-400 truncate">{tx.description}</p>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900">{tx.amount > 0 ? `${tx.amount} ${tx.currency || 'Cc'}` : '-'}</p>
                    <p className="text-[10px] font-mono text-gray-400">{tx.tx_id?.slice(0, 10)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {pendingTxs.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, (pendingTxs.length / (stats?.block_size || 10)) * 100)}%` }}></div>
              </div>
              <p className="text-xs text-gray-400 mt-1 text-center">{pendingTxs.length} de {stats?.block_size || 10} transacciones para auto-minar</p>
            </div>
          )}
        </div>
      )}

      {/* Verify */}
      {tab === 'verify' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4" data-testid="verify-view">
          <h3 className="text-lg font-semibold text-gray-900">Verificar Integridad</h3>
          <p className="text-sm text-gray-500">Recorre toda la cadena verificando hashes y enlaces entre bloques.</p>
          <button onClick={verifyChain} disabled={verifying} data-testid="verify-chain-btn"
            className="bg-gray-900 text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 font-medium">
            {verifying ? 'Verificando...' : 'Verificar Cadena Completa'}
          </button>
          {verification && (
            <div data-testid="verify-result" className={`rounded-lg p-5 ${verification.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{verification.valid ? '✓' : '✗'}</span>
                <div>
                  <p className={`font-semibold ${verification.valid ? 'text-green-800' : 'text-red-800'}`}>{verification.message}</p>
                  <p className="text-sm text-gray-600 mt-1">{verification.blocks_checked} bloques verificados</p>
                </div>
              </div>
              {verification.errors?.length > 0 && (
                <div className="mt-3 space-y-1">
                  {verification.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">Bloque #{e.block}: {e.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search TX */}
      {tab === 'search' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4" data-testid="search-view">
          <h3 className="text-lg font-semibold text-gray-900">Buscar Transaccion</h3>
          <div className="flex gap-2">
            <input type="text" value={searchTx} onChange={(e) => setSearchTx(e.target.value)}
              data-testid="search-tx-input" placeholder="ID de transaccion..."
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm font-mono focus:ring-2 focus:ring-gray-400" />
            <button onClick={searchTransaction} data-testid="search-tx-btn"
              className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 text-sm font-medium">Buscar</button>
          </div>
          {searchResult && (
            <div data-testid="search-result" className={`rounded-lg p-4 text-sm ${searchResult.found ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
              {searchResult.found ? (
                <div>
                  <p className="font-semibold text-blue-800">Transaccion encontrada ({searchResult.status})</p>
                  {searchResult.block_index !== undefined && <p className="text-xs text-gray-600 mt-1">Bloque: #{searchResult.block_index} | Hash: {searchResult.block_hash?.slice(0, 16)}...</p>}
                  <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto">{JSON.stringify(searchResult.transaction, null, 2)}</pre>
                </div>
              ) : (
                <p className="text-gray-600">{searchResult.message || 'Transaccion no encontrada'}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Config (Admin) */}
      {tab === 'config' && isAdmin && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4" data-testid="config-view">
          <h3 className="text-lg font-semibold text-gray-900">Configuracion de Minado</h3>
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dificultad de Minado (1-{stats?.max_difficulty || 5})
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Mayor dificultad = mas ceros al inicio del hash = mas tiempo de minado
              </p>
              <div className="flex items-center gap-4">
                <input type="range" min="1" max={stats?.max_difficulty || 5} value={difficultyInput}
                  onChange={e => setDifficultyInput(parseInt(e.target.value))} data-testid="difficulty-slider"
                  className="flex-1" />
                <div className="text-center min-w-[100px]">
                  <p className="text-2xl font-mono font-bold text-indigo-600">{'0'.repeat(difficultyInput)}{'x'.repeat(6 - difficultyInput)}</p>
                  <p className="text-xs text-gray-400">d={difficultyInput}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={setDifficulty} data-testid="set-difficulty-btn"
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-500 text-sm font-medium">
                Aplicar Dificultad {difficultyInput}
              </button>
              <span className="text-xs text-gray-400 self-center">
                Actual: d={stats?.current_difficulty || 2} | Dificultad {difficultyInput} requiere hash con {difficultyInput} ceros
              </span>
            </div>
          </div>

          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <h4 className="text-sm font-semibold text-amber-800 mb-2">Estimacion de Rendimiento</h4>
            <div className="grid grid-cols-3 gap-3 text-xs text-amber-700">
              <div><strong>d=1:</strong> ~0.01s (rapido)</div>
              <div><strong>d=2:</strong> ~0.05s (normal)</div>
              <div><strong>d=3:</strong> ~1-5s (lento)</div>
              <div><strong>d=4:</strong> ~30-60s (muy lento)</div>
              <div><strong>d=5:</strong> ~5-30min (extremo)</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockchainComponent;
